import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Save } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import type { SchemaField } from "../../components/SchemaEditor/SchemaEditor";
import { Select } from "../../components/Select/Select";
import {
  SegmentedToggle,
  type SegmentedOption,
} from "../../components/SegmentedToggle/SegmentedToggle";
import { useToast } from "../../components/Toast/toastContext";

import { ApiError, describeError } from "../../lib/errors";
import { updateGuild } from "./api";
import { GuildMetadataEditor } from "./GuildMetadataEditor";
import {
  GuildSaveReviewDialog,
  type SettingsChange,
} from "./GuildSaveReviewDialog";
import { diffMetadata, metadataEqual } from "./metadata";
import type { GuildDetail, GuildVisibility, UpdateGuildInput } from "./types";
import { visibilityLabel } from "./visibility";
import styles from "./GuildSettingsForm.module.css";

// === Visibility options ============================================== //
// The segmented control of docs/mockups/guild-detail.html, extended with the
// third join mode: Open / Invite only / Closed.
const VISIBILITY_OPTIONS: SegmentedOption<GuildVisibility>[] = [
  { value: "open", label: "Open" },
  { value: "invite_only", label: "Invite only" },
  { value: "closed", label: "Closed" },
];

// === Component ====================================================== //
// A child of GuildDetailPage (Task 13). It owns the Settings card, the
// Metadata card and the sticky save bar; the parent owns the shared query and
// re-syncs it from the `onSaved` callback. The dirty baseline is the `guild`
// prop — the parent remounts this component (keyed on the saved record) after a
// save so the useState initializers re-read the freshly-saved values.
//
// The Metadata card lives here rather than on the page because there is one
// save bar per page: it has to count both cards' changes, and Save has to send
// one PATCH carrying both.

interface GuildSettingsFormProps {
  projectId: string;
  guildId: string;
  /** The loaded guild — the form's baseline for dirty tracking. */
  guild: GuildDetail;
  /** games.config.guild.metadata_schema — empty means raw-JSON-only metadata. */
  metadataSchema: SchemaField[];
  /** games.config.guild.recommendations.requirement_guild_key — "" when unset. */
  requirementKey: string;
  /**
   * games.config.guild_assets.allowed_logos — the asset IDs this project's
   * logo field offers. Empty means the project declared none, and the field
   * falls back to free text: the list is a hint for the operator, not a rule
   * the server keeps, so an empty one must not lock the field.
   */
  allowedLogos: string[];
  /** Called with the updated guild after a successful PATCH. */
  onSaved: (updated: GuildDetail) => void;
}

export function GuildSettingsForm({
  projectId,
  guildId,
  guild,
  metadataSchema,
  requirementKey,
  allowedLogos,
  onSaved,
}: GuildSettingsFormProps) {
  const { push } = useToast();

  // A server that predates guilds.metadata must not hand the uncontrolled
  // editor an undefined object.
  const guildMetadata = guild.metadata ?? {};

  // --- Settings field state ------------------------------------------ //
  const [name, setName] = useState(guild.name);
  const [description, setDescription] = useState(guild.description);
  // The column is nullable; the field is a string. "" stands for "no logo",
  // and the same "" is what a select left on its placeholder reads as — which
  // is why a guild wearing something outside the whitelist is not dirty until
  // the operator picks a replacement.
  const [logo, setLogo] = useState(guild.logo ?? "");
  const [visibility, setVisibility] = useState<GuildVisibility>(
    guild.visibility,
  );

  // A whitelist the project actually filled in turns the field into a picker;
  // an empty one leaves it free text, the players page's avatar rule verbatim.
  const useLogoSelect = allowedLogos.length > 0;

  // --- Metadata editor state ------------------------------------------ //
  // GuildMetadataEditor is uncontrolled after mount: we mirror its latest
  // object here, track whether it has been touched, and remount it (via
  // metadataKey) on discard so it re-seeds from the guild's record.
  const [metadata, setMetadata] =
    useState<Record<string, unknown>>(guildMetadata);
  const [metadataDirty, setMetadataDirty] = useState(false);
  const [metadataValid, setMetadataValid] = useState(true);
  const [metadataKey, setMetadataKey] = useState(0);

  // The review dialog in front of a save that touched metadata.
  const [reviewOpen, setReviewOpen] = useState(false);

  // --- Per-field validation ------------------------------------------ //
  const trimmedName = name.trim();
  const nameError =
    trimmedName.length < 1 || trimmedName.length > 64
      ? "Guild name must be between 1 and 64 characters."
      : undefined;
  // Description is sent raw (untrimmed) — validate the raw length to match.
  const descError =
    description.length > 512
      ? "Description must be 512 characters or fewer."
      : undefined;

  // --- Dirty tracking ------------------------------------------------ //
  // Measured on the trimmed value, which is what a save would send: typing a
  // trailing space is not a change, and the review dialog must not open on a
  // line that reads `name: Iron Vanguard → Iron Vanguard`.
  const nameDirty = trimmedName !== guild.name;
  const descDirty = description !== guild.description;
  const logoDirty = logo !== (guild.logo ?? "");
  const visDirty = visibility !== guild.visibility;

  const dirty = nameDirty || descDirty || logoDirty || visDirty || metadataDirty;
  const changeCount =
    (nameDirty ? 1 : 0) +
    (descDirty ? 1 : 0) +
    (logoDirty ? 1 : 0) +
    (visDirty ? 1 : 0) +
    (metadataDirty ? 1 : 0);

  // Not gated on `metadataDirty`: broken JSON in the Raw editor never reaches
  // `onChange` at all, so the card can be unsavable while the parent has seen
  // no change to count. The card reports itself valid until the operator
  // touches it, which is what keeps a guild whose stored value already
  // disagrees with its schema renameable from this page.
  const errorCount =
    (nameError ? 1 : 0) + (descError ? 1 : 0) + (metadataValid ? 0 : 1);
  const hasError = errorCount > 0;

  // === Save ========================================================== //
  const mutation = useMutation({
    mutationFn: (body: UpdateGuildInput) =>
      updateGuild(projectId, guildId, body),
  });

  // ApiError save failure → a generic alert above the card (the
  // PlayerDetailForm serverGenericError pattern).
  const serverError = mutation.error instanceof ApiError ? mutation.error : null;
  const serverGenericError = serverError
    ? describeError(serverError.code)
    : undefined;

  /**
   * Save. An edit that touched metadata stops here and shows the review dialog
   * first — the game reads that object, and a wrong key in it looks like
   * nothing at all on screen. An edit that only moved name / description /
   * visibility goes straight up, exactly as it did before this card existed.
   */
  function doSave() {
    if (hasError || !dirty) return;
    if (metadataDirty) {
      setReviewOpen(true);
      return;
    }
    submit();
  }

  function submit() {
    // Only the dirty fields are sent. `description` is sent as the raw current
    // value (untrimmed) — parity with the domain. `metadata` goes up whole:
    // the column is replaced, not merged.
    const body: UpdateGuildInput = {};
    if (nameDirty) body.name = trimmedName;
    if (descDirty) body.description = description;
    if (logoDirty) body.logo = logo;
    if (visDirty) body.visibility = visibility;
    if (metadataDirty) body.metadata = metadata;
    mutation.mutate(body, {
      onSuccess: (updated) => {
        setReviewOpen(false);
        push({ variant: "success", message: "Guild updated" });
        onSaved(updated);
      },
      // No onError: the dialog stays open and shows the reason itself. The
      // page-level alert sits above the Settings card, a card and a members
      // table away from the sticky bar the operator pressed Save in.
    });
  }

  function doDiscard() {
    setName(guild.name);
    setDescription(guild.description);
    setLogo(guild.logo ?? "");
    setVisibility(guild.visibility);
    setMetadata(guildMetadata);
    setMetadataDirty(false);
    setMetadataValid(true);
    // Remount the editor so it re-seeds from the guild's record.
    setMetadataKey((k) => k + 1);
    mutation.reset();
  }

  // What the review dialog lists. Built only when it is open: the diff walks
  // both objects, and nothing needs it while the operator is still typing.
  const settingsChanges: SettingsChange[] = reviewOpen
    ? [
        ...(nameDirty
          ? [{ key: "name", before: guild.name, after: trimmedName }]
          : []),
        ...(descDirty
          ? [
              {
                key: "description",
                before: guild.description,
                after: description,
              },
            ]
          : []),
        ...(logoDirty
          ? [
              {
                key: "logo",
                // The column's null reads as "—" in the review, not as an
                // empty cell: the operator is being shown what the guild wore
                // before, and nothing at all is one of the answers.
                before: guild.logo ?? "—",
                after: logo,
              },
            ]
          : []),
        ...(visDirty
          ? [
              {
                key: "visibility",
                before: guild.visibility,
                after: visibility,
              },
            ]
          : []),
      ]
    : [];
  const metadataChanges = reviewOpen
    ? diffMetadata(
        guildMetadata,
        metadata,
        metadataSchema.map((f) => f.key),
      )
    : { rows: [], unchanged: 0 };

  return (
    <>
      {serverGenericError && !reviewOpen && (
        <div className={styles.errorAlert}>
          <Alert variant="danger" title="Couldn't save changes">
            {serverGenericError}
          </Alert>
        </div>
      )}

      {/* ───────── Settings ───────── */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>Settings</h3>
          <span className={styles.cardSub}>
            name · logo · description · visibility
          </span>
        </div>
        <div className={styles.cardBody}>
          {/* Name */}
          <div className={`${styles.inputGroup} ${styles.fieldGap}`}>
            <label htmlFor="guild-name">
              Name
              {nameDirty && (
                <span className={styles.modDot} title="modified" aria-hidden />
              )}
            </label>
            <input
              id="guild-name"
              className={`${styles.input} ${nameError ? styles.invalid : ""} ${
                !nameError && nameDirty ? styles.modified : ""
              }`}
              value={name}
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? "guild-name-err" : undefined}
              onChange={(e) => setName(e.target.value)}
            />
            {nameError && (
              <div className={styles.fieldError} id="guild-name-err">
                {nameError}
              </div>
            )}
          </div>

          {/* Description */}
          <div className={`${styles.inputGroup} ${styles.fieldGap}`}>
            <label htmlFor="guild-description">
              Description
              {descDirty && (
                <span className={styles.modDot} title="modified" aria-hidden />
              )}
            </label>
            <textarea
              id="guild-description"
              className={`${styles.textarea} ${
                descError ? styles.invalid : ""
              } ${!descError && descDirty ? styles.modified : ""}`}
              spellCheck={false}
              value={description}
              aria-invalid={descError ? true : undefined}
              aria-describedby={descError ? "guild-description-err" : undefined}
              onChange={(e) => setDescription(e.target.value)}
            />
            {descError && (
              <div className={styles.fieldError} id="guild-description-err">
                {descError}
              </div>
            )}
          </div>

          {/* Logo ─────────────────────────────────────────────────────────
              The players page's Avatar field, guild-side: a select while the
              project's whitelist has entries, free text while it has none.
              What it CANNOT do is take a logo off a guild — an empty string
              does not pass the select, exactly as it does not for an avatar.
              A logo outside the whitelist leaves the select on its placeholder
              and stays untouched until somebody picks a replacement; the
              identity card above is where its real value is readable. */}
          <div className={`${styles.inputGroup} ${styles.fieldGap}`}>
            <label htmlFor="guild-logo">
              Logo
              {logoDirty && (
                <span className={styles.modDot} title="modified" aria-hidden />
              )}
            </label>
            {useLogoSelect ? (
              <Select
                id="guild-logo"
                value={logo}
                onChange={setLogo}
                options={allowedLogos.map((l) => ({ value: l, label: l }))}
                placeholder="Select a logo…"
                monospaceValue
              />
            ) : (
              <input
                id="guild-logo"
                className={`${styles.input} ${styles.mono} ${
                  !logoDirty ? "" : styles.modified
                }`}
                type="text"
                value={logo}
                onChange={(e) => setLogo(e.target.value)}
              />
            )}
            {useLogoSelect ? (
              <div className={styles.helper}>
                From the project&apos;s allowed-logos whitelist.
              </div>
            ) : (
              <div className={styles.helper}>
                Whitelist <code className={styles.code}>allowed_logos</code> is
                empty → free-text input, any string accepted.
              </div>
            )}
          </div>

          {/* Visibility */}
          <div className={styles.inputGroup}>
            <label id="guild-visibility-label">
              Visibility
              {visDirty && (
                <span className={styles.modDot} title="modified" aria-hidden />
              )}
            </label>
            <SegmentedToggle
              options={VISIBILITY_OPTIONS}
              value={visibility}
              onChange={setVisibility}
              aria-label="Visibility"
            />
            {visDirty ? (
              <div className={styles.helper}>
                Was{" "}
                <code className={styles.code}>
                  {visibilityLabel(guild.visibility)}
                </code>
                .
              </div>
            ) : (
              <div className={styles.helper}>
                Open — anyone can join. Invite only — players need an invite.
                Closed — players apply and a leader or officer decides.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ───────── Metadata ─────────
          Between Settings and Members, sharing this form's save bar. */}
      <GuildMetadataEditor
        key={metadataKey}
        schema={metadataSchema}
        requirementKey={requirementKey}
        value={guildMetadata}
        onChange={(next) => {
          setMetadata(next);
          setMetadataDirty(!metadataEqual(next, guildMetadata));
        }}
        onValidityChange={setMetadataValid}
      />

      {/* ───────── Sticky save bar ─────────
          Shown for an error as well as for a change: a Raw editor left holding
          broken JSON counts as neither dirty nor saveable, and without the bar
          there is no Discard on screen to undo it with. */}
      {(dirty || hasError) && (
        <div
          className={styles.saveBar}
          role="region"
          aria-label="Unsaved changes"
        >
          <div className={styles.inner}>
            <div
              className={`${styles.status} ${hasError ? styles.statusErr : ""}`}
            >
              <span className={styles.statusDot} aria-hidden />
              {hasError
                ? `Fix ${errorCount} ${
                    errorCount === 1 ? "error" : "errors"
                  } to save`
                : `${changeCount} unsaved ${
                    changeCount === 1 ? "change" : "changes"
                  }`}
            </div>
            <div className={styles.saveActions}>
              <Button
                variant="ghost"
                onClick={doDiscard}
                disabled={mutation.isPending}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                onClick={doSave}
                disabled={hasError || !dirty || mutation.isPending}
                loading={mutation.isPending}
                icon={
                  !mutation.isPending ? (
                    <Save size={13} aria-hidden />
                  ) : undefined
                }
              >
                {mutation.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {reviewOpen && (
        <GuildSaveReviewDialog
          guildName={guild.name}
          settings={settingsChanges}
          metadata={metadataChanges.rows}
          unchangedMetadata={metadataChanges.unchanged}
          saving={mutation.isPending}
          error={serverGenericError}
          onCancel={() => setReviewOpen(false)}
          onConfirm={submit}
        />
      )}
    </>
  );
}
